/*
 * Client Portal — API contract stubs.
 *
 * These types are the frontend↔backend contract for the portal (journey map:
 * skylarq-ai.github.io/skylarq-outbound/site/gtm-engine/journey-map.html).
 * Mock adapters implement them today; backend endpoints implement them as they
 * land. Changing a shape here is a contract change — flag it in the PR, not
 * after build.
 *
 * Verified-live today (dev @ 8d8ed2ad):
 *   GET /pipeline/projects/{id}/replies  → CampaignReply[] (mirrors CampaignReplyItem)
 * Everything else is a proposed shape awaiting backend implementation.
 */

import type { TopUpPlan } from '../../types';
import type { CampaignBrief as PipelineCampaignBrief } from '../../types/pipeline';

/** GET /workspace/me — proposed. Permissions follow the D1 action-flag model. */
export interface PortalWorkspace {
  workspaceId: string;
  name: string;
  // clientEntityId intentionally absent: clients-as-entities is a KG/ops concern,
  // never part of the portal contract (ruled 7/21; see admin clients registry).
  permissions: {
    approveGates: boolean;
    draftReplies: boolean;
    curateTam: boolean;
    /** self-serve tier flags land here later (editDrafts, launchWithinCaps, …) */
    [flag: string]: boolean;
  };
  /** SKY-489 fail-loud: ingest sources whose sync is dead or failing, under
   * vendor-neutral names (SKY-470). Aggregates computed from them (weekly counts
   * ← 'email_replies') render "being verified", never a possibly-undercounted number. */
  sourcesBeingVerified: string[];
  /** Workspace chose "don't ask again" on the finish-setup nag. */
  setupDeclined: boolean;
  /** A tam/copy step read failed server-side: the stepper booleans can't be trusted
   * to gate on, so the portal never shows the waitlist while this is true. */
  stepsUnknown: boolean;
}

export type CampaignChannel = 'email' | 'linkedin' | 'dinner' | 'event';

export type PipelineStage =
  | 'intake' | 'icp' | 'source_enrich' | 'copy' | 'qa'
  | 'infra' | 'live' | 'replies' | 'done';

/** Client-recognizable stages (SKY-510 decision). Early internal stages
 * (intake/icp/source_enrich) never render on client surfaces — a half-sourced
 * list isn't a campaign to the client yet. Stage-based only: a true
 * internal/test flag needs a backend classification (recorded, not MVP).
 * Consumers: Overview campaigns table, Send-history campaign filter. */
export const CLIENT_STAGES: ReadonlySet<string> = new Set(['copy', 'qa', 'infra', 'live', 'replies', 'done']);

/** Workspace-scoped campaign list — proposed (client projection of /pipeline/board). */
export interface PortalCampaign {
  id: string;
  name: string;
  pipelineStage: PipelineStage;
  /** SKY-474 — drives the pause/resume control and the paused banner. */
  status: 'active' | 'paused';
  needsClientReview: boolean;
  /** Channel legs are DECLARED at creation, never inferred. */
  legs: {
    /** opaque email-leg ref (SKY-470: vendor-neutral — never names the sending vendor) */
    emailCampaignRef?: string;
    linkedinSequenceRef?: string;
    /** latest declared-sequencer funnel counts (SKY-439) */
    linkedin?: {
      accountId: string;
      invited: number;
      accepted: number;
      replied: number;
      messagesSent: number;
      syncedAt: string | null;
      /** SKY-489: 'being_verified' = the snapshot behind these counts is too old
       * to trust; counts arrive zeroed — render "being verified", never the zeros. */
      state: 'fresh' | 'being_verified';
    };
    event?: { name: string; date?: string; kind: 'conference' | 'dinner' | 'side-event' };
  };
  stats: {
    sent: number;
    opens: number;
    replies: number;
    meetings: number;
    /** dinner/event campaigns report RSVP framing instead of sends */
    invited?: number;
    accepted?: number;
    declined?: number;
  };
  /** freshness stamp — render "as of {statsSyncedAt}" */
  statsSyncedAt: string | null;
  /** SKY-489 fail-loud: 'being_verified' = the stats snapshot is too old to
   * trust; numbers arrive zeroed server-side — render "being verified", never the zeros. */
  statsState: 'fresh' | 'being_verified';
  updatedAt: string;
  /** audience formula shown under the name, e.g. "BIO attendees × TAM · 108 people" */
  audience?: string;
  /** SKY-511 card meta: linked list member count, else provider audience count */
  leadCount?: number | null;
  /** how many approved people this campaign reaches, and how many it leaves out; both null
   * until its launch has enrolled anyone */
  reachCount?: number | null;
  leftOutCount?: number | null;
  /** first tracked send day */
  startedAt?: string | null;
  /** The catalog conference this campaign sources from; its portal login lives here. */
  eventId?: string | null;
  /** 'temporal' goes live from its outreach sequence's Send for real, never a prepared launch. */
  sequencerEngine?: OutreachSequenceState['engine'];
}

/** SKY-533: one roster row from GET /portal/campaigns/{id}/linkedin-leads.
 * last_* fields carry the row grammar (who · step · when · outcome); message
 * text is never in this payload — drill into the timeline endpoint for it. */
export interface LinkedInRosterLead {
  leadId: string;
  linkedinUrl: string | null;
  name: string | null;
  company: string | null;
  title: string | null;
  stage: string;
  personalizationLine: string | null;
  variant: string | null;
  entityId: string | null;
  lastEventType: 'invite_sent' | 'invite_accepted' | 'message_sent' | 'reply_received' | null;
  lastStep: number | null;
  lastAt: string | null;
}

/** SKY-533 page envelope. account_id null = the campaign has no LinkedIn leg
 * (unstamped instance, SKY-531) — render the honest empty state, not an error. */
export interface LinkedInLeadsPage {
  accountId: string | null;
  total: number;
  limit: number;
  offset: number;
  leads: LinkedInRosterLead[];
}

/** One event on a lead's LinkedIn timeline (GET /portal/linkedin-leads/{id}/timeline).
 * text null = no template resolved (honest, SKY-532); template_inferred = the
 * send predates capture and text is the earliest captured version. */
export interface LinkedInTimelineEvent {
  type: 'invite_sent' | 'invite_accepted' | 'message_sent' | 'reply_received' | 'history_received' | 'history_sent' | string;
  at: string;
  step: number | null;
  text: string | null;
  templateKey: string | null;
  templateInferred: boolean;
}

export interface LinkedInLeadTimeline {
  leadId: string;
  accountId: string;
  linkedinUrl: string | null;
  name: string | null;
  company: string | null;
  title: string | null;
  stage: string;
  entityId: string | null;
  personalizationLine: string | null;
  variant: string | null;
  segment: string | null;
  events: LinkedInTimelineEvent[];
}

/** SKY-536 (frozen contract): one sequence step. text = what was CAPTURED —
 * inferred means the sends predate capture (shown text is the current
 * captured version); null = uncaptured (SKY-532 vintage) — the step scaffold
 * still renders. stepDelayDays is null in v1: delays aren't persisted. */
export interface LinkedInSequenceStep {
  step: number;
  label: string;
  stepDelayDays: number | null;
  inferred: boolean;
  variants: { key: string; text: string | null }[];
}

export interface LinkedInSequence {
  accountId: string | null;
  capturedAt: string | null;
  steps: LinkedInSequenceStep[];
}

/** SKY-535: one day of LinkedIn work — per-day DELTAS between cumulative
 * snapshots. clamped = an upstream rebuild floored the delta to 0; spannedDays
 * = this bar carries a measurement gap; partial = today, still accruing. */
export interface LinkedInReportDay {
  date: string;
  invited: number;
  accepted: number;
  messaged: number;
  replied: number;
  clamped: boolean;
  spannedDays: number | null;
  partial: boolean;
}

/** SKY-535 report envelope. state 'no_campaign' = no LinkedIn leg (the honest
 * empty replacing "appears after the first sync"); 'being_verified' withholds
 * the funnel but the series still renders. funnel = INDEPENDENT tallies, not a
 * monotone funnel (replied can exceed messaged; accepted can exceed invited on
 * auto-connects) — render tolerant, never clamp. */
export interface LinkedInReport {
  state: 'fresh' | 'being_verified' | 'no_campaign';
  accountId: string | null;
  funnel: { invited: number; accepted: number; messaged: number; replied: number } | null;
  series: LinkedInReportDay[];
  days: number;
  syncedAt: string | null;
}

/** GET /portal/campaigns/{id}/leads — LIVE (SKY-455). One row of the campaign's real
 * email-provider audience, synced every 2h. Only email is reliably present — render what exists. */
export interface CampaignLead {
  /** null for a LinkedIn-sourced person, who legitimately has no address. */
  email: string | null;
  name: string | null;
  company: string | null;
  title: string | null;
  /** SKY-467: KG link (resolved server-side by email + workspace); null = no history */
  entityId: string | null;
}

/** One day of email work (SKY-477), derived server-side as a delta between cumulative
 * snapshots — so a day with no measurement is absent, never a fabricated zero. */
export interface CampaignDayPoint {
  /** UTC calendar day, YYYY-MM-DD */
  date: string;
  sent: number;
  opens: number;
  replies: number;
  /** counters went backwards upstream; the day was floored at 0 */
  clamped?: boolean;
  /** the previous measured day was n days back — this bar covers all of them */
  spannedDays?: number | null;
  /** day still in progress — rendered muted, never as a drop */
  partial?: boolean;
}

/** GET /portal/campaigns/{id}/stats-history — LIVE (SKY-477).
 * 'no_campaign' = no email leg has ever synced. 'not_started' = live but the first send is
 * still ahead, with sendingStartsOn carrying the day. 'being_verified' = SKY-489 fail-loud:
 * funnel is null because the numbers behind it are too old to trust — the series still
 * renders, since past days are not a claim about current numbers. */
export interface CampaignStatsHistory {
  state: 'fresh' | 'being_verified' | 'no_campaign' | 'not_started';
  /** ISO date the first send is scheduled for; only set with state 'not_started' */
  sendingStartsOn?: string | null;
  /** days until that date, counted in the campaign's own timezone: 0 is today. The client
   * must not recompute this from its own clock — the two disagree for hours at a time. */
  sendingStartsInDays?: number | null;
  funnel: {
    leads: number;
    /** null = vendor counter was event-based (exceeded the list size) with no
     * trustworthy unique substitute — the stage is skipped, never fabricated */
    contacted: number | null;
    sent: number;
    opens: number;
    replies: number;
    meetings: number;
    /** annotation on sent, not a funnel stage */
    bounced: number;
  } | null;
  series: CampaignDayPoint[];
  days: number;
}

/** GET /portal/campaigns/{id}/copy — LIVE (SKY-488). One version of one copy track;
 * all versions arrive, newest first per track. Rows are immutable — an edit is a new
 * version, and approval (slice 2) pins a specific version id. */
export interface CopyVariant {
  id: string;
  /** e.g. "Email 1 — intro", "LinkedIn DM" */
  track: string;
  version: number;
  subject: string | null;
  body: string;
  createdAt: string;
  /** Latest decision on THIS version; null = awaiting review. A new version
   * always arrives with null — approval never carries across versions. */
  decision: 'approve' | 'reject' | null;
  decisionReason: string | null;
  decidedAt: string | null;
  /** AI rules check (SKY-488 slice 6); null = not checked — render nothing,
   * never "clean". Flags inform the reviewer; they never block approval. */
  ruleCheck: { status: 'clean' | 'flagged'; flags: { rule: string; note: string }[] } | null;
}

/** GET/POST/PATCH/DELETE /portal/rules — LIVE (SKY-488). One rule of engagement. */
export interface EngagementRule {
  id: string;
  rule: string;
  createdAt: string | null;
}

/** Ratified disposition vocabulary (prototype OUTCOME control). Backend column is unconstrained. */
export const DISPOSITIONS = ['interested', 'nurture', 'referred', 'not_a_fit'] as const;
export type Disposition = (typeof DISPOSITIONS)[number];
/** Outcomes that end the conversation — choosing one also resolves the thread. */
export const TERMINAL_DISPOSITIONS: readonly Disposition[] = ['not_a_fit', 'nurture'];

/** GET /pipeline/projects/{id}/replies — LIVE. Mirrors backend CampaignReplyItem. */
export interface ReplyDraftState {
  body: string;
  status: 'draft' | 'sent' | 'dismissed';
  sentAt: string | null;
  dismissReason: string | null;
  /** 'auto' = written by the triage runner, never sent (SKY-400). */
  source: 'client' | 'auto';
}

export interface CampaignReply {
  id: string;
  provider: 'email' | 'linkedin';
  threadId: string | null;
  direction: 'inbound' | 'outbound';
  sender: { name: string | null; title: string | null; company: string | null; entityId: string | null; email: string | null };
  subject: string | null;
  body: string | null;
  /** Mapper-derived, NOT on the wire (SKY-464): DOMPurify-sanitized HTML of the
   * raw body when the message arrived as HTML; null/absent for plain text.
   * `body` stays the plain-text projection for one-line previews. */
  bodyHtml?: string | null;
  occurredAt: string | null;
  read: boolean;
  disposition: string | null;
  /** Client triage intent (SKY-451), stored on the latest inbound; null → derive
   * from drafts/disposition exactly as before (#586). */
  triageStatus: 'handled' | null;
  /** The workspace mailbox this message ran through (email only today). */
  mailboxEmail: string | null;
  /** False when a cc'd participant wrote rather than the campaign's lead. */
  fromLead: boolean;
  /** Comma-separated, as the provider stores them. */
  cc: string | null;
  bcc: string | null;
  /** Set only on the optimistic row for a reply this client just sent. Stored
   * bcc may be an account-level archive address that is not theirs to see, so
   * Bcc renders for this row alone. */
  clientAuthored?: boolean;
  /** A triage run is filed and hasn't answered yet (SKY-400). */
  triageInFlight: boolean;
  draft: ReplyDraftState | null;
}

/** One send-history ledger line (SKY-498): an actual send (sent/replied) or a
 * "nothing sent" human decision (held/skipped). */
export interface SendHistoryItem {
  id: string;
  channel: 'email' | 'linkedin';
  occurredAt: string | null;
  recipient: string | null;
  displayName: string | null;
  title: string | null;
  company: string | null;
  entityId: string | null;
  projectId: string | null;
  campaignName: string | null;
  /** Sends carry the 0-based sequence position; held/skipped rows carry the
   * projected cohort in nextStep instead. */
  stepIndex: number | null;
  nextStep: string | null;
  mailboxEmail: string | null;
  outcome: 'sent' | 'replied' | 'held' | 'skipped';
  repliedAt: string | null;
  reason: string | null;
  threadId: string | null;
  subject: string | null;
  body: string | null;
  /** Mapper-derived (same convention as CampaignReply.bodyHtml): sanitized HTML
   * for the row expand when the message arrived as HTML. */
  bodyHtml?: string | null;
}

/** The actual message behind one activity-feed row (SKY-527), fetched on click
 * via getTimelineMessage. Never inlined in the feed. */
export interface TimelineMessage {
  subject: string | null;
  body: string | null;
  /** Mapper-derived (same convention as CampaignReply.bodyHtml): sanitized HTML
   * when the message arrived as HTML; body stays the plain-text branch. */
  bodyHtml: string | null;
  /** body is a stored prefix of a longer email (ingest caps stored bodies). */
  bodyTruncated: boolean;
  direction: 'inbound' | 'outbound';
  counterpart: string | null;
  occurredAt: string;
  /** Set only when the message belongs to a thread Conversations can open. */
  threadId: string | null;
  campaignId: string | null;
}

export interface SendHistoryPage {
  asOf: string | null;
  /** SKY-489: 'being_verified' = the reply poll behind this ledger went stale. */
  state: 'fresh' | 'being_verified';
  rows: SendHistoryItem[];
  /** Composite keyset cursor for the next (older) page; null = exhausted. */
  nextBefore: string | null;
}

export interface SendHistoryQuery {
  projectId?: string;
  q?: string;
  mailbox?: string;
  outcome?: 'any' | 'sent' | 'held' | 'skipped';
  since?: string;
  before?: string;
  limit?: number;
}

/** POST /replies/{id}/draft — proposed. Operator sends; client never sends directly in v1. */
export interface ReplyDraft {
  replyId: string;
  body: string;
  status: 'draft' | 'sent';
  createdAt: string;
}

/** GET /workspaces/{id}/tam — proposed. */
/** GET /portal/campaigns/{id}/sequence (SKY-512): read-only steps + waits. */
export interface SequenceStep {
  index: number;
  subject: string | null;
  body: string | null;
  delayDays: number;
}
export interface CampaignSequence {
  state: 'fresh' | 'being_verified' | 'no_campaign';
  steps: SequenceStep[];
  planSummary?: string | null;
}

/** GET /portal/campaigns/{id}/sending-settings (SKY-476/512): pacing, read-only here. */
export interface SendingWindow {
  days: number[];
  from: string | null;
  to: string | null;
  timezone: string | null;
}
export interface SendingSettings {
  state: 'fresh' | 'being_verified' | 'no_campaign';
  window: SendingWindow | null;
  dailyCap: number | null;
  ceiling: number;
}

/** /portal/campaigns/{id}/outreach-sequence: a sequence we run and the client edits. */
export type OutreachChannel = 'email' | 'linkedin_invite' | 'linkedin_message' | 'app_request';
export interface OutreachStep {
  id: string;
  position: number;
  channel: OutreachChannel;
  delayDays: number;
  subject: string | null;
  body: string;
  /** Email steps: enrolled people with client-approved copy for this email; null when unread. */
  leadCopyCount: number | null;
  /** The first day (YYYY-MM-DD, sequence timezone) the step may send; null for no limit. */
  notBefore: string | null;
  /** An operator's edit to a running step, held unsent until the client approves it. */
  pendingChange: OutreachStepPendingChange | null;
  pendingAt: string | null;
}
export interface OutreachStepPendingChange {
  subject?: string;
  body?: string;
  delayDays?: number;
  notBefore?: string | null;
  withNote?: boolean;
}
export type OutreachEnrollmentStatus = 'active' | 'paused' | 'replied' | 'finished' | 'stopped' | 'unknown';
export interface OutreachSequence {
  id: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  mode: 'simulated' | 'live';
  timezone: string;
  windowStart: string;
  windowEnd: string;
  dailyCap: number;
  senderUserId: string | null;
  sendWeekdaysOnly: boolean;
  steps: OutreachStep[];
  enrollmentCounts: Record<OutreachEnrollmentStatus, number>;
  /** How many are in progress once it goes live: active, plus those the test run finished or paused for an auto-reply. */
  liveReach: number;
  /** The highest email_<n> any enrolled person has client-approved copy for; 0 for none, null when unread. */
  writtenEmailTracks: number | null;
  stoppedReasons: Record<string, number>;
  heldReasons: Record<string, number>;
  pausedReasons: Record<string, number>;
  finishedReasons: Record<string, number>;
  /** People whose last send failed, by cause. */
  failedReasons: Partial<Record<OutreachFailure, number>>;
  /** Placeholders a step may use: the standard ones, then this sequence's custom ones. */
  variables: string[];
  appendSignature: boolean;
  signatureStatus: 'fetched' | 'none' | 'unavailable' | null;
  editable: boolean;
  /** The last day anything sends (YYYY-MM-DD, sequence timezone); null for no hard stop. */
  lastSendOn: string | null;
  noHardStop: boolean;
  /** A conference campaign: it needs a last sending day unless an operator opted out. */
  hardStopRequired: boolean;
  /** The event has an event-app login on file, so app requests can be sent. */
  appRequests: boolean;
  /** Values filled in at send time that are the same for everyone, e.g. a dinner's time and venue. */
  variableValues: Record<string, string>;
  /** Questions worth a second look that do not block anything, e.g. an odd dinner start time. */
  warnings: string[];
}
/** GET outreach-sequence: 'external' keeps the provider's read-only view. */
export interface OutreachSequenceState {
  engine: 'external' | 'temporal';
  canOperate: boolean;
  sequence: OutreachSequence | null;
  needsAttention: string | null;
}
export type OutreachFailure = 'reconnect' | 'rate_limited' | 'recipient_rejected' | 'other';
/** A workspace member who could send the sequence. */
export interface OutreachSender {
  userId: string;
  name: string | null;
  email: string | null;
  gmailConnected: boolean;
  linkedinConnected: boolean;
}
export interface OutreachSettingsInput {
  mode?: OutreachSequence['mode'];
  timezone?: string;
  windowStart?: string;
  windowEnd?: string;
  dailyCap?: number;
  senderUserId?: string;
  sendWeekdaysOnly?: boolean;
}
export interface OutreachSettingsResult {
  sequence: OutreachSequence;
  warnings: string[];
}
export interface OutreachEnrollResult {
  enrolled: number;
  skipped: { entityId: string; reason: string }[];
}
export interface OutreachStepInput {
  channel: OutreachChannel;
  delayDays: number;
  subject: string | null;
  body: string;
  notBefore?: string | null;
}
/** Server-side variable rules, mirrored: a fallback's last segment is a variable only when it is
 * one of these, and {{signature}} belongs in an email body only. */
export const OUTREACH_STANDARD_VARIABLES = [
  'first_name', 'last_name', 'name', 'email', 'company', 'title', 'linkedin_url', 'phone',
  'personalization', 'signature', 'unsubscribe_link', 'firstName', 'job_title', 'unsubscribe_url',
] as const;
/** A dinner's send-time variables; like the standard ones, they may close a fallback chain. */
export const OUTREACH_DINNER_VARIABLES = ['dinner_when', 'dinner_where'] as const;
/** Server-side limits, mirrored for inline validation. */
export const OUTREACH_LIMITS = {
  maxSteps: 10,
  maxDelayDays: 60,
  body: { email: 20000, linkedin_invite: 300, linkedin_message: 8000, app_request: 1000 } satisfies Record<OutreachChannel, number>,
};

/** GET /portal/tam query filters (SKY-456/453) — applied server-side, in the DB. */
/** The tier vocabulary, in rank order; the single home for the union. */
export const TIERS = ['T1', 'T2', 'T3', 'skip'] as const;
export type Tier = (typeof TIERS)[number];

export interface TamFilters {
  q?: string;
  status?: 'all' | 'new' | 'suppressed';
  replied?: boolean;
  /** narrow to one lead list's members (SKY-453) */
  listId?: string;
  /** Narrow to the gone-quiet set, ordered oldest touch first. */
  stale?: boolean;
  /** Narrow to one tier. */
  tier?: Tier;
  /** Fetch contacted-channel chips (People page only — costs an extra read). */
  withChannels?: boolean;
  /** Header sort; omitted = tier rank (or staleness order under stale). */
  sort?: 'name' | 'last_touch';
  dir?: 'asc' | 'desc';
}

/** GET /portal/tam/{entityId} (SKY-513): the person-page header block. Every
 * field is absent when unknown — the page omits it rather than fabricating. */
export interface PersonColleague {
  entityId: string;
  name: string;
  title: string | null;
}

export interface PersonDetail {
  entityId: string;
  name: string;
  title: string | null;
  company: string | null;
  /** works_at target — the account card links to /portal/accounts/{id} */
  companyEntityId: string | null;
  location: string | null;
  tier: Tier | null;
  suppressed: boolean;
  replied: boolean;
  provisional: boolean;
  addedAt: string | null;
  email: string | null;
  linkedinUrl: string | null;
  colleagues: PersonColleague[];
}

/** GET /portal/tam/counts (SKY-513): facet counts within the search + list
 * scope, plus the active combination's total. null in the UI = counts
 * unavailable — pills render without numbers, never with fabricated zeros. */
export interface TamCounts {
  total: number;
  all: number;
  new: number;
  suppressed: number;
  replied: number;
  stale: number;
}

/** GET /portal/lead-lists — LIVE (SKY-453). One upload batch; campaigns reference lists. */
export interface LeadList {
  id: string;
  name: string;
  kind: string;
  createdAt: string | null;
  gtmProjectId: string | null;
  memberCount: number;
  /** Review state: memberCount = kept + cut + undecided. */
  keptCount: number;
  cutCount: number;
  undecidedCount: number;
  /** list-level client sign-off; null = not yet approved (gates campaign start) */
  approvedAt: string | null;
}

export type LeadDisposition = 'keep' | 'cut' | 'undecided';

export interface TamPerson {
  entityId: string;
  name: string;
  title: string | null;
  company: string | null;
  /** SKY-513: works_at target — company text links to the account page when set */
  companyEntityId: string | null;
  /** SKY-513 contact column: from entity_identifiers, absent when unknown */
  email: string | null;
  linkedinUrl: string | null;
  /** null = no tier assigned yet (operator curation) — render as unset, never a fabricated default */
  tier: Tier | null;
  suppressed: boolean;
  lastSignalAt: string | null;
  campaignIds: string[];
  /** SKY-456: ≥1 inbound campaign reply matched to this person */
  replied: boolean;
  /** SKY-456: when the person was added to the TAM (drives the "New" filter) */
  addedAt: string | null;
  /** SKY-488 Keep/Cut — only set when the request filtered by listId; null otherwise */
  disposition: LeadDisposition | null;
  /** One line on why the filtering step picked them — list-scoped like disposition */
  matchReason: string | null;
  /** SKY-499: created by upload, not yet confirmed — renders "being verified" */
  provisional: boolean;
  /** SKY-475: last touch either direction (sequence send, their own email, a reply) — derived, never entered */
  lastTouchAt: string | null;
  /** SKY-475: enrolled + last touch older than the org threshold (suppressed people never flag) */
  stale: boolean;
  /** Channels actually contacted on — outbound touches, never inferred from an address */
  channels: string[];
}

/** GET /portal/tam/{entityId}/last-touch — the person-panel block (SKY-475). */
export interface PersonLastTouch {
  lastTouchAt: string | null;
  stale: boolean;
  /** earliest still-scheduled send, if any */
  nextSendDueOn: string | null;
  nextSendStep: string | null;
  /** nothing queued because their reply halted the sequence */
  stoppedOnReply: boolean;
}

/** Phase-0 landing: the engagement stepper (empty state IS the product). */
export interface EngagementStep {
  key: 'intake' | 'tam' | 'first_campaign' | 'campaign_live';
  label: string;
  status: 'done' | 'in_progress' | 'pending';
}

/** One due send for today (SKY-471) — eligibility, never a provider commitment. */
export interface TodayQueueItem {
  id: string;
  campaignId: string | null;
  campaignName: string | null;
  channel: 'email' | 'linkedin' | string;
  person: string | null;
  title: string | null;
  company: string | null;
  nextStep: string;
  /** Human cohort line rendered verbatim: "first touch" / "no reply after 4 days". */
  reason: string;
  status: 'scheduled' | 'held' | 'skipped' | 'sent' | string;
  /** false = beyond the day's send budget — part of the "up to N" pool. */
  inCapacity: boolean;
  /** Client approve-all stamp (SKY-496); cleared by re-projection when the set changes. */
  acknowledgedAt?: string | null;
  /** False when sent from the client's own inbox: there is nothing to hold at a provider. */
  holdable?: boolean;
  /** Projected by a simulated sequence: nothing is actually sent. */
  simulated?: boolean;
}

/** Today's due sends. `state` being_verified means the read failed, so an empty `rows`
 * must not be shown as a quiet day; `truncated` means the read hit its ceiling. */
export interface TodayQueuePage {
  state: 'fresh' | 'being_verified';
  rows: TodayQueueItem[];
  truncated: boolean;
}

/** Partial-failure report from a campaign-scoped bulk action (SKY-496). */
export interface TodayQueueBulkResult {
  action: 'hold_all' | 'acknowledge_all';
  affected: number;
  failed: number;
  skipped: number;
}

/** One pause/resume audit line (SKY-474) — feeds the campaign header history. */
export interface CampaignPauseEvent {
  action: 'paused' | 'resumed';
  reason: string | null;
  occurredAt: string;
  actorName: string | null;
  /** actor belongs to the client workspace ("paused by you") */
  actorIsClient: boolean;
}

/** GET /portal/campaigns/{id}/brief: the brief with its source tags and, once approved, the plan. */
export interface CampaignBrief {
  fields: PipelineCampaignBrief['fields'];
  confirmedAt: string | null;
  confirmedBy: string | null;
  conferenceName: string | null;
  conferenceStartMissing: boolean;
  planSummary: string | null;
  peopleWanted: number;
}

/** A credit pack on sale, from GET /portal/credits/top-up-packs. */
export type TopUpPack = Pick<TopUpPlan, 'id' | 'name' | 'pricing' | 'credits'>;

/** GET /portal/credits: the workspace balance, what approvals hold, and what is left. */
export interface WorkspaceCredits {
  balance: number;
  held: number;
  available: number;
  exempt: boolean;
  trial_taken: boolean;
  trial: { credits: number; ends_at: string } | null;
  next_expiry: { credits: number; expires_at: string } | null;
}

export interface CreditUsageRow {
  createdAt: string;
  campaignName: string | null;
  action: string;
  label: string | null;
  credits: number;
  exempt: boolean;
}

/** GET /portal/credits/usage: one page of the ledger, newest first. */
export interface CreditUsagePage {
  rows: CreditUsageRow[];
  total: number;
}

/** GET /portal/campaigns/:id/credit-estimate: what approving the brief would hold, and the
 * bounds on how many people it may ask for. */
export interface CreditEstimate {
  credits: number;
  conferenceCredits: number;
  personCredits: number;
  likelyMatches: number;
  peopleMinimum: number;
  available: number;
  held: number;
  exempt: boolean;
}

/** GET /portal/campaigns/:id/research-credits: who research found and what it charged. */
export interface ResearchCredits {
  wanted: number;
  found: number;
  charged: number;
  returned: number;
}

export interface PlanOption {
  id: string;
  name: string;
  pricing: number;
  credits: number;
  is_popular: boolean | null;
}

export interface CurrentPlan {
  plan: PlanOption;
  status: 'active' | 'past_due' | 'unpaid';
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  scheduledPlanName: string | null;
}

/** GET /portal/plan: the workspace's paid plan, the plans on offer, and who may change it. */
export interface WorkspacePlan {
  current: CurrentPlan | null;
  plans: PlanOption[];
  canManage: boolean;
  exempt: boolean;
  freeCredits: number | null;
}

/** GET /billing/get-payment-method: what pays for the workspace's plan; card fields only for cards. */
export interface PaymentMethod {
  type: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
}

/** Result of a LinkedIn connect step — login or code verification. */
export interface LinkedInConnectResult {
  status: string; // 'success' | 'verification_required' | provider passthrough
  message: string | null;
  checkpointType: string | null;
}

export interface PortalIntegration {
  provider: string;
  name: string;
  connected: boolean;
  account: string | null;
  status: 'connected' | 'expired' | 'not_connected' | string;
  connectedAt: string | null;
  /** Granola only: latest transcript sync — its connection evidence (no
   * credential ever exists server-side; the desktop app holds the token). */
  lastSyncedAt?: string | null;
  /** Workspace-level tools (HubSpot): whether the viewer holds the credential
   * Disconnect severs; null/undefined where ownership does not apply. */
  ownedByViewer?: boolean | null;
  /** Who connected it, set only when the viewer does not own it. */
  connectedBy?: string | null;
}

export const EVENT_HOST_STATUSES = ['ok', 'expired', 'challenged', 'not_connected'] as const;
export type EventHostStatus = (typeof EVENT_HOST_STATUSES)[number];

/** The workspace's signed-in session on one event-host site. */
export interface EventHostConnection {
  site: string;
  name: string;
  status: EventHostStatus;
  savedAt: string | null;
  lastVerifiedAt: string | null;
}

export const CLAUDE_CREDENTIAL_STATUSES = ['pending_validation', 'active', 'invalid'] as const;
export type ClaudeCredentialStatus = (typeof CLAUDE_CREDENTIAL_STATUSES)[number];

/** The workspace's Claude Code setup token, as the backend reports it. */
export interface ClaudeCredential {
  id: string;
  accountLabel: string;
  status: ClaudeCredentialStatus;
  lastValidatedAt: string | null;
  createdAt: string;
}

/** SKY-478 account rollup row (portal Accounts list). */
export interface PortalAccount {
  entityId: string;
  name: string;
  domain: string | null;
  peopleCount: number;
  replyingCount: number;
  lastTouchAt: string | null;
}

/** GET /portal/accounts (SKY-514): one page + the unpaged total (footer count). */
export interface AccountsPage {
  total: number;
  accounts: PortalAccount[];
}

export interface AccountClaimSource {
  label: string; // vendor-neutral: 'email' | 'meeting' | 'linkedin' | 'your CRM' | 'your team'
  date: string;  // ISO date the source was seen
}

/** One brief claim. Server-side contract: every claim on the wire carries at
 * least one client-safe source — unsourced claims never arrive. */
export interface AccountClaim {
  section: string;
  text: string;
  sources: AccountClaimSource[];
}

export type AccountStage = 'mapped' | 'contacted' | 'replied' | 'meeting';

/** SKY-499 upload outcome — counts always reconcile: total = created + merged + failed. */
export interface UploadListResult {
  created: number;
  merged: number;
  failed: number;
  total: number;
  errors: { index: number | null; reason: string; row: number | null }[];
  leadListId: string | null;
}

export interface PortalAccountDetail extends PortalAccount {
  /** Derived four-stage pipeline position; 'customer' is never claimed. */
  stage: AccountStage;
  hasBrief: boolean;
  briefUpdatedAt: string | null;
  claims: AccountClaim[];
}

/** A correction the client filed, matched back to claims by excerpt. */
export interface AccountCorrection {
  claimExcerpt: string;
  correctionText: string;
  occurredAt: string;
}

/** SKY-452 composer tone chips. */
export type ReplyTone = 'shorter' | 'warmer' | 'propose_times' | 'decline';

/** Notification switches; slackConnected says whether posts can land at all. */
export interface NotificationSettings {
  dailyBrief: boolean;
  replyAlerts: boolean;
  progress: boolean;
  email: boolean;
  slackConnected: boolean;
}

/** One event row for the directory and detail header. */
export interface PortalEvent {
  id: string;
  name: string;
  dateStart: string | null;
  dateEnd: string | null;
  city: string | null;
  country: string | null;
  venue: string | null;
  vertical: string | null;
  eventType: string | null;
  categories: string[] | null;
  eventFormat: string | null;
  attendeeCount: number | null;
  confirmedCount: number | null;
  /** The caller's campaign for this conference; only the detail read carries it. */
  projectId?: string | null;
}

/** A catalog conference as the signup step offers it: name and place, no attendee data. */
export interface ConferenceOption {
  id: string;
  name: string;
  city: string | null;
  country: string | null;
  dateStart: string | null;
  dateEnd: string | null;
}

/** The signup step's pick: a catalog id, or the conference the client is adding. */
export interface ConferenceChoice {
  eventId?: string;
  name?: string;
  city?: string;
  dateStart?: string;
  dateEnd?: string;
}

export type BriefStatus = 'not_started' | 'drafting' | 'ready';

/** What a waiting signup told us, and how far review and the brief have got. */
export interface SignupSummary {
  companyName: string | null;
  companyWebsite: string | null;
  role: string | null;
  conference: { name: string; city: string | null; dateStart: string | null; dateEnd: string | null } | null;
  briefStatus: BriefStatus;
  approved: boolean;
}

export interface EventFilters {
  kind?: string;
  format?: string;
  category?: string;
  /** City or country prefix. */
  place?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface EventsPage {
  items: PortalEvent[];
  total: number;
}

export type EventSort = 'size' | 'date' | 'name';

/** What an admin knows about a conference they could not find in the directory. */
export interface EventLookupInput {
  name: string;
  city?: string;
  country?: string;
  dateStart?: string;
  dateEnd?: string;
  website?: string;
}

/** One agent run as GET /runs/{id} reports it. */
export interface RunOutcome {
  status: string;
  error: string | null;
  result: string | null;
  verdict: string | null;
  verdictData: Record<string, unknown>;
}

/** One graded attendee on an event's detail page. */
export interface PortalAttendee {
  id: string;
  fullName: string | null;
  title: string | null;
  orgName: string | null;
  linkedinUrl: string | null;
  /** Null for someone only this workspace places here, who has no shared grade yet. */
  confidenceGrade: 'confirmed' | 'likely' | 'inferred' | null;
  role: string;
  /** True when this workspace added the person itself, graded or not. */
  addedByTeam: boolean;
  /** pending, verified or rejected for a row this workspace added; null otherwise. */
  additionStatus: string | null;
}

export interface AttendeesPage {
  items: PortalAttendee[];
  total: number;
}

/** Server side list controls for an event's people; arrays are OR within a group, AND across groups. */
export interface AttendeeQuery {
  page?: number;
  limit?: number;
  sort?: 'name' | 'grade' | 'role';
  role?: string[];
  grade?: string[];
  hasLinkedin?: boolean | null;
  q?: string;
}

/** Where one attendee row came from, fetched on demand for the source card. */
export interface PortalAttendeeSource {
  method: string | null;
  mode: string | null;
  url: string | null;
  evidence: string | null;
  provider: string | null;
  foundAt: string | null;
}

/** Whole event counts per facet value, for the filter dropdowns. */
export interface AttendeeFacets {
  role: Record<string, number>;
  grade: Record<string, number>;
  linkedin: Record<string, number>;
}

export interface CompanyQuery {
  page?: number;
  limit?: number;
  sort?: 'name' | 'role' | 'tier';
  role?: string[];
  tier?: string[];
  hasDomain?: boolean | null;
  q?: string;
}

export interface CompanyFacets {
  role: Record<string, number>;
  tier: Record<string, number>;
}

/** One sponsoring or exhibiting company on an event's detail page. */
export interface PortalCompany {
  id: string;
  name: string | null;
  domain: string | null;
  tier: string | null;
  website: string | null;
  industry: string | null;
  country: string | null;
  role: string;
}

export interface CompaniesPage {
  items: PortalCompany[];
  total: number;
}

/** Whether a sign in is stored for an event's registration portal; the secret never leaves the server. */
export interface PortalLoginStatus {
  exists: boolean;
  host: string | null;
  url: string | null;
}

export interface PortalLoginSaved extends PortalLoginStatus {
  /** Sourcing runs waiting at this event's login wall that the save continued. */
  resumed: number;
}

export interface PortalLoginInput {
  username: string;
  password: string;
  url: string;
}

/** GET /portal/campaigns/{id}/lead-copy/people — one person's latest message on one track,
 * as the client reviews it. `flag` is the writer's own mark; decision null = awaiting. */
export interface LeadCopyMessage {
  id: string;
  track: string;
  version: number;
  subject: string | null;
  body: string;
  flag: 'no_facts' | 'check_failed' | null;
  decision: 'approve' | 'reject' | null;
  decisionReason: string | null;
}

export interface LeadCopyPerson {
  entityId: string;
  name: string;
  company: string | null;
  messages: LeadCopyMessage[];
}

/** The campaign a conference now has, and whether its brief draft started. */
export interface EventGoing {
  projectId: string;
  drafting: boolean;
}

/** The lines every email of the workspace ends with, else the default we would use. */
export interface SignOff {
  signOff: string | null;
}

/** What the workspace's company does or sells, as its campaign copy describes it. */
export interface CompanyDescription {
  companyDescription: string | null;
}

/** Where a person stands with the client; changes requested wins over approved over pending. */
export type LeadCopyStatus = 'pending' | 'approved' | 'changes_requested';

export type LeadCopyStatusCounts = Record<LeadCopyStatus, number>;

/** One page of people; `asOf` is the server moment the page was read and `counts` covers everyone. */
export interface LeadCopyPeoplePage {
  asOf: string;
  counts: LeadCopyStatusCounts;
  people: LeadCopyPerson[];
}

/** GET /portal/campaigns/{id}/lead-copy — per track, how many people's latest versions stand where. */
export interface LeadCopyTrackSummary {
  track: string;
  leads: number;
  approved: number;
  rejected: number;
  awaiting: number;
}

export type DinnerMode = 'invite_only' | 'hosted' | 'external';
export type CalendarStatus = 'ok' | 'no_host' | 'needs_access' | 'needs_time' | 'event_removed' | 'error';
export const GUEST_STATUSES = [
  'invited', 'registered', 'approved', 'waitlist', 'declined', 'attended', 'no_show',
] as const;
export type GuestStatus = (typeof GUEST_STATUSES)[number];
export type GuestOrigin = 'conference_reply' | 'walk_in' | 'invited';
export type EventPlatform = 'luma' | 'partiful' | 'eventbrite' | 'other';

/** GET /portal/campaigns/{id}/dinner: the dinner's logistics and where its syncs stand. */
export interface DinnerBrief {
  name: string;
  mode: DinnerMode;
  platform: EventPlatform | null;
  eventUrl: string | null;
  startsAt: string | null;
  timezone: string | null;
  venueName: string | null;
  address: string | null;
  seats: number | null;
  confirmCap: number | null;
  hostUserId: string | null;
  calendarStatus: CalendarStatus | null;
  calendarSyncedAt: string | null;
  guestsSyncedAt: string | null;
}

/** PATCH /portal/campaigns/{id}/dinner: the fields to write, in the API's names. */
export interface DinnerUpdate {
  starts_at?: string | null;
  timezone?: string | null;
  venue_name?: string | null;
  address?: string | null;
  seats?: number | null;
  /** Left out, the server sets the default cap for the seats sent. */
  confirm_cap?: number | null;
  host_user_id?: string | null;
  event_url?: string | null;
}

export type GuestCounts = Record<GuestStatus | 'walk_ins', number>;

export interface DinnerGuest {
  entityId: string;
  /** The person's name, or their email when we hold no name. */
  name: string | null;
  company: string | null;
  status: GuestStatus;
  knownFrom: GuestOrigin;
  rsvpNote: string | null;
  /** For a walk-in, the person they may already be, until the client confirms or dismisses it. */
  suggestedEntityId: string | null;
  suggestedName: string | null;
}

/** GET /portal/campaigns/{id}/guests: counts for the whole dinner, and one page of guests. */
export interface DinnerGuestPage {
  counts: GuestCounts;
  guests: DinnerGuest[];
}

/** GET /portal/campaigns/{id}/launch: the prepared launch, whether it can go live or who it waits
 * on, and why anyone approved is left out. people is null for LinkedIn. */
export interface LaunchSummary {
  ready: boolean;
  live: boolean;
  waitingOn: 'you' | 'team' | null;
  people: number | null;
  leftOut: string;
  senders: string[];
  startDate: string | null;
}

/** What an add or upload did; `batchId` is null when nothing landed. */
export interface AttendeeAdditionResult {
  batchId: string | null;
  added: number;
  alreadyFound: number;
  rejected: number;
  rejects: { row: number | null; reason: string }[];
}

/** One attendee a user adds by hand. */
export interface NewAttendee {
  fullName: string;
  orgName: string;
  title?: string;
  linkedinUrl?: string;
  email?: string;
  role: 'attendee' | 'speaker';
  evidenceUrl?: string;
}
